'use client';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { useColors } from '@osmosis/tailwind-ui/helpers/useColors';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { ZIndex } from '@osmosis/tailwind-ui/helpers/zIndex';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons/faSparkles';
import { Citation, Tag as TagProps } from '@osmosis/shared/types/learn';
import { Tooltip } from '@osmosis/tailwind-ui/components/tooltip/Tooltip';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import useModal from '../../../hooks/useModal';
import { useUserContext } from '../../../context/UserContext';
import { Thumbnail, Updates } from '@osmosis/shared/types/learndb/learnData';
import { text0, textGray900 } from '@osmosis/tailwind-ui/styles/text';
import dynamic from 'next/dynamic';
import { useVideoThumbnail } from '@osmosis/nextjs-components/hooks/useVideoThumbnail';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { useContentSize } from '@osmosis/nextjs-components/context/ContentSizeContext';
import OsmosisAIButton from '../../ai/OsmosisAIButton';
import { videoLastUpdated } from '../VideoInformation';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import { safeFormat } from '../dateUtils';
import { isWithinFiveYears } from '../../../lib/util/date';

const FeedbackForm = dynamic(() => import('../FeedbackForm'), { ssr: false });
const VideoInformation = dynamic(() => import('../VideoInformation'), {
  ssr: false,
});

interface LearnHeaderInfoProps {
  isAnswerPage?: boolean;
  isVideoOnlyPage?: boolean;
  hasVideo?: boolean;
  title: string;
  views: number;
  /** Pre-computed from LearnHeader display rules*/
  showHeaderButtons?: boolean;
  showViews?: boolean;
  learnNodeID: number;
  thumbnails?: Thumbnail;
  url?: string;
  tags: TagProps[];
  citations: Citation;
  modified: string;
  updates?: Updates[];
  learnPageCreationDate?: string;
  lastUpdated?: string | undefined;
  lastReviewed?: string | undefined;
  infoModalIsVideoPage?: boolean;
  programAccess?: string[];
}

const LearnHeaderButtons = dynamic(() => import('../LearnHeaderButtons'), {
  ssr: false,
});

/**
 * Renders Left side/lower side of the learn page header.
 */
const LearnHeaderInfo = (props: LearnHeaderInfoProps): JSX.Element => {
  const {
    isAnswerPage,
    title,
    views,
    showHeaderButtons,
    showViews,
    learnNodeID,
    thumbnails,
    url,
    tags,
    citations,
    modified,
    updates,
    lastUpdated,
    lastReviewed,
    learnPageCreationDate,
    infoModalIsVideoPage,
    programAccess,
    hasVideo = false,
    isVideoOnlyPage = false,
  } = props;

  const { user } = useUserContext();
  const thumbnail = useVideoThumbnail(thumbnails);

  const isInternal = !!Object.keys(user?.admin || {}).length;
  const isEditor = !!user?.admin?.editor;

  const colors = useColors();
  const ariaLabel = 'Video information';

  const intl = useIntl();

  const { md } = useContentSize();

  // Video Information Modal
  const videoInfoModal = useModal();
  const { toggle: toggleInfoModal } = videoInfoModal;

  // Feedback Form Modal
  const feedbackModalProps = useModal();
  const { toggle: toggleFeedbackModal } = feedbackModalProps;
  const toBeRetiredTag = tags.find(
    ({ tag }) => tag.toLowerCase() === 'to be retired',
  );

  const h4Style = ['mb-1', textGray900, 'opacity-75'].join(' ');

  // show OAI button in the learn header only for notes pages (no video) and non-video-only pages
  const showOAIButton = !hasVideo && !isVideoOnlyPage;

  const getDisplayDateInfo = () => {
    if (lastReviewed && isWithinFiveYears(lastReviewed)) {
      return {
        date: lastReviewed,
        type: 'lastReviewed' as const,
        dataTestId: 'video-info-last-reviewed',
        ariaLabelId: 'video-information.last-reviewed-label',
        ariaLabelDefaultMessage: 'Last reviewed: {date}',
      };
    }

    if (lastUpdated && isWithinFiveYears(lastUpdated)) {
      return {
        date: lastUpdated,
        type: 'lastUpdated' as const,
        dataTestId: videoLastUpdated,
        ariaLabelId: 'video-information.last-updated-label',
        ariaLabelDefaultMessage: 'Last updated: {date}',
      };
    }

    if (learnPageCreationDate && isWithinFiveYears(learnPageCreationDate)) {
      return {
        date: learnPageCreationDate,
        type: 'pageCreated' as const,
        dataTestId: 'video-info-page-created',
        ariaLabelId: 'video-information.page-created-label',
        ariaLabelDefaultMessage: 'Page created: {date}',
      };
    }

    return null;
  };
  const displayDateInfo = getDisplayDateInfo();

  return (
    <>
      <Flex
        className={cn(
          'mb-2 items-center',
          isEditor ? 'md:min-w-[500px]' : '',
          md ? 'justify-end' : 'justify-start pt-2',
        )}
      >
        <Flex className="items-center">
          {!isAnswerPage && (
            <IconButton
              round
              variant="transparent"
              icon={<Icon icon={faInfoCircle} size={16} />}
              onClick={toggleInfoModal}
              data-testid="learn-header-info-button"
              data-tip
              data-tooltip-id="info-icon"
              aria-label={ariaLabel}
              className={cn(h4Style, md ? '' : 'h-6 w-6', 'mb-0')}
            />
          )}

          <Tooltip
            id="info-icon"
            style={{
              backgroundColor: colors.text,
              color: colors.textInvert,
              zIndex: ZIndex.MODAL,
            }}
          >
            <P color="inherit" className={`text-[13px]`}>
              {title}
            </P>
          </Tooltip>

          {/* Last reviewed cell */}
          {/* last updated cell - if no lastReviewed date shown */}
          {displayDateInfo && (
            <Box
              data-testid={displayDateInfo.dataTestId}
              className={`mr-4 ${isAnswerPage ? 'hidden md:flex' : ''}`}
              aria-label={intl.formatMessage(
                {
                  id: displayDateInfo.ariaLabelId,
                  defaultMessage: displayDateInfo.ariaLabelDefaultMessage,
                },
                { date: safeFormat(displayDateInfo.date) },
              )}
            >
              <span className={`${h4Style} font-bold text-sm`}>
                {(() => {
                  const dateValues = {
                    date: (
                      <FormattedDate
                        key="date"
                        value={new Date(displayDateInfo.date)}
                        year="numeric"
                        month="long"
                        day="2-digit"
                      />
                    ),
                  };
                  if (displayDateInfo.type === 'lastReviewed') {
                    return (
                      <FormattedMessage
                        id="learn.video.last_reviewed"
                        defaultMessage="Last reviewed: {date}"
                        values={dateValues}
                      />
                    );
                  }
                  if (displayDateInfo.type === 'lastUpdated') {
                    return (
                      <FormattedMessage
                        id="learn.video.last_updated"
                        defaultMessage="Last updated: {date}"
                        values={dateValues}
                      />
                    );
                  }
                  return (
                    <FormattedMessage
                      id="learn.video.page_created"
                      defaultMessage="Page created: {date}"
                      values={dateValues}
                    />
                  );
                })()}
              </span>
            </Box>
          )}
        </Flex>
        {showHeaderButtons && (
          <LearnHeaderButtons
            title={title}
            learnNodeID={learnNodeID}
            thumbnail={thumbnail}
            url={url}
            isEditor={isEditor}
            isInternal={isInternal}
            toBeRetiredTag={toBeRetiredTag}
            programAccess={programAccess}
            showOAIButton={showOAIButton}
          />
        )}
        {isAnswerPage && (
          <OsmosisAIButton
            leftIcon={
              <Icon icon={faSparkles} className={`mr-1 ${text0}`} size={14} />
            }
            label={intl.formatMessage({
              id: 'osmosis-ai-btn.answer-page.label',
              defaultMessage: 'Dive deeper with AI',
            })}
            classNames={{
              container: 'w-48',
              button: 'border-none [&>p]:!text-sm',
            }}
            // Answer pages are publicly accessible — always show to logged-out users as a signup driver
            showLoggedOut
            source="Answer"
          />
        )}
      </Flex>
      {videoInfoModal.isVisible && (
        <VideoInformation
          {...videoInfoModal}
          loggedIn={!!user}
          title={title}
          citations={citations}
          learnPageCreationDate={learnPageCreationDate}
          modified={modified}
          lastUpdated={props.lastUpdated}
          isVideoPage={infoModalIsVideoPage}
          updates={updates}
          toggleFeedbackModal={toggleFeedbackModal}
          isFeedbackModalVisible={feedbackModalProps.isVisible}
          data-testid="learn-header-video-info"
          views={views}
          showViews={showViews}
        />
      )}
      {feedbackModalProps.isVisible && (
        <FeedbackForm
          {...feedbackModalProps}
          videoTitle={title}
          nodeID={learnNodeID}
          subjectText={intl.formatMessage(
            {
              id: 'learn.header.feedback_subject_video',
              defaultMessage: 'Subject: Feedback on video -- {videoTitle}',
            },
            { videoTitle: title },
          )}
        />
      )}
    </>
  );
};

export default LearnHeaderInfo;
